import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  BrokerSchedules,
  ChipData,
  DataInProgress,
  DisableButton,
  EmitNotificationData,
  NgxDateRangePicker,
  NotificationData,
  SchedulesSelected,
  ShipTypeEnum,
} from '@models';
import { QuestionI, TypeDataInProgress, WorkingHour } from '@models';
import { dateUTC } from '@utilities';
import * as moment from 'moment';
moment.locale('es-mx');
import { ScheduleVisitService } from '../schedule-visit.service';
import { DatePickerLocale, DatePickerMaxDate } from './locale.config';
import { ScheduleSocketService } from '@services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ggi-journey-select-datetime',
  templateUrl: './select-datetime.component.html',
  styleUrls: ['./select-datetime.component.scss'],
})
export class SelectDatetimeComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() workingHours: WorkingHour[] = [];
  @Input() isPropertyFlow: boolean;
  @Output() canUserContinue = new EventEmitter<boolean>();

  public schedules: ChipData[] = [];

  public readonly locale = DatePickerLocale;
  public readonly maxDate = moment().add(DatePickerMaxDate, 'days');
  public readonly today = moment();

  public dateSelected = '';
  public questionI: QuestionI;
  public scheduleSelected: string;
  public answer: string;
  public openAnswer: string;

  private isOpenAnswer: boolean;

  private readonly dataVisit: Partial<DataInProgress> = JSON.parse(
    localStorage.getItem('ggiScheduleVisit')
  );
  public subscriptions: Subscription[] = [];
  public preSelectedDate: moment.Moment;
  private socketId = '';
  private roomName = '';
  private disabledButtonsList: DisableButton[] = [];
  private schedulesSelectedBySocket: SchedulesSelected[] = [];
  private brokerSchedules: BrokerSchedules[];
  private selectedBroker: BrokerSchedules;
  private dataBySocket: NotificationData = {
    datetime: '',
    idBroker: '',
    socketId: '',
  };

  constructor(
    private readonly service: ScheduleVisitService,
    private readonly scheduleSocket: ScheduleSocketService
  ) {}

  public ngOnInit(): void {
    this.changeDate(this.today);

    this.getBrokersSchedule(this.today);

    this.dateSelected = dateUTC(this.today, 'MX');
    this.preSelectedDate = this.today;

    if (this.isPropertyFlow) {
      this.getQuestion();
    }
  }

  public ngAfterViewInit(): void {
    this.initSockets();
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  private initSockets(): void {
    const [todayDay, todayMonth, todayYear] = this.today
      .format('DD-MM-YYYY')
      .split('-');
    const { day, month, year, idProperty } = {
      day: todayDay,
      month: todayMonth,
      year: todayYear,
      idProperty: this.dataVisit.propertyId,
    };
    this.roomName = `Room-${day}-${month}-${year}-${idProperty}`;
    this.scheduleSocket.connectToScheduleVisit(this.roomName);
    this.detectScheduleShipSelected();
    this.detectScheduleShipDeselected();
    this.subscriptions.push(
      this.scheduleSocket.socketId.subscribe((data) => {
        this.socketId = data;
      })
    );
  }

  private detectScheduleShipSelected(): void {
    this.subscriptions.push(
      this.scheduleSocket.selectScheduleShip.subscribe((data) => {
        if (data.datetime !== '') {
          const selectedDaySocket = dateUTC(data.datetime, 'MX');
          const dataShipValue = moment(data?.datetime).format('H:mm');
          if (this.socketId !== data?.socketId) {
            this.addScheduleSelectedBySocket(selectedDaySocket, dataShipValue);
            if (selectedDaySocket === this.dateSelected) {
              this.removeScheduleBroker(data.idBroker, dataShipValue);
              if (!this.brokersWithFreeSchedule(dataShipValue).length) {
                this.addDisabledButton(selectedDaySocket, dataShipValue);
                this.checkIfButtonMustBeDisabled();
              }
            }
          } else {
            this.dataBySocket = data;
          }
        }
      })
    );
  }

  private addDisabledButton(date: string, value: string): void {
    const selected = this.disabledButtonsList.filter((e) => e.date === date);
    if (selected.length > 0) {
      selected[0].values.push(value);
    } else {
      const data: DisableButton = {
        date,
        values: [value],
      };
      this.disabledButtonsList.push(data);
    }
  }

  private removeDisabledButton(fecha: string, valor: string): void {
    const selectedButtons = this.disabledButtonsList.filter(
      (e) => e.date === fecha
    );
    if (selectedButtons.length > 0) {
      selectedButtons[0].values = selectedButtons[0].values.filter(
        (e) => e !== valor
      );
    }
  }

  private brokersWithFreeSchedule(dataShipValue: string): BrokerSchedules[] {
    const data = this.brokerSchedules.filter((e) =>
      e.parseSlot.includes(dataShipValue)
    );
    return data;
  }

  private detectScheduleShipDeselected(): void {
    this.subscriptions.push(
      this.scheduleSocket.deselectScheduleShip.subscribe((data) => {
        if (data?.socketId !== '' && this.socketId !== data?.socketId) {
          const dataShipValue = moment(data?.datetime).format('H:mm');
          const parseDate = dateUTC(data?.datetime, 'MX');
          if (parseDate === this.dateSelected) {
            this.enableButton(dataShipValue);
            this.addScheduleBroker(data.idBroker, dataShipValue);
          }
          this.removeScheduleSelectedBySocket(parseDate, dataShipValue);
          this.removeDisabledButton(parseDate, dataShipValue);
        }
      })
    );
  }

  private removeScheduleBroker(idBroker: string, dataShipValue: string): void {
    const selectedBrokers = this.brokerSchedules.filter(
      (e) => e.broker === idBroker
    );
    selectedBrokers[0].parseSlot = selectedBrokers[0].parseSlot.filter(
      (e) => e !== dataShipValue
    );
  }

  private addScheduleBroker(idBroker: string, dataShipValue: string): void {
    const selectedBrokers = this.brokerSchedules.filter(
      (e) => e.broker === idBroker
    );
    if (selectedBrokers.length > 0) {
      selectedBrokers[0].parseSlot.push(dataShipValue);
    }
  }

  private addScheduleSelectedBySocket(date: string, chip: string): void {
    const selected = this.schedulesSelectedBySocket.filter(
      (e) => e.date === date
    );
    if (selected.length > 0) {
      selected[0].selectedChips.push(chip);
    } else {
      const data = {
        date,
        selectedChips: [chip],
      };
      this.schedulesSelectedBySocket.push(data);
    }
  }

  private removeScheduleSelectedBySocket(
    date: string,
    dataShipValue: string
  ): void {
    const selected = this.schedulesSelectedBySocket.filter(
      (e) => e.date === date
    );
    if (selected.length > 0) {
      selected[0].selectedChips = selected[0].selectedChips.filter(
        (e) => e !== dataShipValue
      );
    }
  }

  public changeDate(date: any): void {
    const day = date as NgxDateRangePicker;

    this.scheduleSelected = undefined;
    this.canUserContinue.next(false);

    if (!day.hasOwnProperty('startDate')) {
      this.service.saveDataInProgress(
        { day: date.format('YYYY-MM-DD') },
        TypeDataInProgress.day
      );
      return;
    }

    const { startDate } = day;
    this.getBrokersSchedule(startDate);

    this.dateSelected = dateUTC(startDate, 'MX');
    this.preSelectedDate = startDate;

    this.service.saveDataInProgress(
      { day: startDate.format('YYYY-MM-DD') },
      TypeDataInProgress.day
    );
    this.updateChipDeselected();
  }

  private getBrokersSchedule(date: moment.Moment): void {
    const data = {
      date: date.format('DD-MM-YYYY'),
      propertyId: this.dataVisit.propertyId,
    };
    this.subscriptions.push(
      this.service.getCalendarAvailable(data).subscribe((resp) => {
        const slosts: string[] = [];
        this.brokerSchedules = resp.response.schedules;
        this.brokerSchedules.map((e) => {
          const slots: string[] = [];
          e.slots.map((item) => {
            const chip = moment(item).utc().locale('es').format('H:mm');
            slots.push(chip);
            if (!slosts.includes(chip)) {
              slosts.push(chip);
            }
          });
          e.parseSlot = slots;
        });
        this.createChips(slosts);
      })
    );
  }

  public createChips(slostByDay: string[]): void {
    this.schedules = [];
    const sortedSlots = slostByDay.sort(
      (a, b) => moment(a, 'H:mm').unix() - moment(b, 'H:mm').unix()
    );
    if (dateUTC(this.today, 'MX') === this.dateSelected) {
      const currentHour = moment().format('H:mm');
      const slotsHourLess = sortedSlots.filter(
        (e) => moment(e, 'H:mm').unix() > moment(currentHour, 'H:mm').unix()
      );
      this.addToSchedules(slotsHourLess);
    } else {
      this.addToSchedules(sortedSlots);
    }
    this.checkIfButtonMustBeDisabled();
  }

  public addToSchedules(slostByDay: string[]): void {
    slostByDay.map((e) => {
      const data: ChipData = {
        value: e,
        isDisabled: false,
      };
      this.schedules.push(data);
    });
  }

  public checkIfButtonMustBeDisabled(): void {
    const selectedButton = this.disabledButtonsList.filter(
      (e) => e.date === this.dateSelected
    );
    if (selectedButton.length > 0) {
      this.schedules.forEach((e) => {
        if (selectedButton[0].values.includes(e.value)) {
          e.isDisabled = true;
        } else {
          e.isDisabled = false;
        }
      });
    }
  }

  public enableButton(dataShipValue: string): void {
    const selectedButton = this.schedules.filter(
      (e) => e.value === dataShipValue
    );
    if (selectedButton.length > 0) {
      selectedButton[0].isDisabled = false;
    }
  }

  public selectSchedule(schedule: string): void {
    this.scheduleSelected = schedule;
    this.updateChipDeselected();
    this.service.saveDataInProgress(
      { hour: schedule },
      TypeDataInProgress.hour
    );

    if (!this.isPropertyFlow || this.answer) {
      this.canUserContinue.next(true);
    }
    this.updateShipSelected(
      ShipTypeEnum.timeslot_selected,
      this.scheduleSelected
    );
  }

  public selectBroker(): void {
    const availableBrokers = this.brokerSchedules.filter((e) =>
      e.parseSlot.includes(this.scheduleSelected)
    );
    this.selectedBroker =
      availableBrokers[Math.floor(Math.random() * availableBrokers.length)];
    this.service.saveDataInProgress(
      { broker: this.selectedBroker?.broker },
      TypeDataInProgress.broker
    );
  }

  private updateShipSelected(
    typeAction: string,
    selectedSchedule?: string
  ): void {
    this.selectBroker();
    const dataSchedule = selectedSchedule ? selectedSchedule.split(':') : '';
    const data: EmitNotificationData = {
      roomName: this.roomName,
      datetime: selectedSchedule
        ? this.parseDate(dataSchedule[0], dataSchedule[1])
        : '',
      shipType: typeAction,
      socketId: this.socketId,
      brokerId: this.selectedBroker?.broker,
    };
    this.scheduleSocket.notification(data);
  }

  public updateChipDeselected(): void {
    const { datetime, idBroker, socketId } = this.dataBySocket;
    const data: EmitNotificationData = {
      roomName: this.roomName,
      datetime,
      shipType: ShipTypeEnum.timeslot_deselected,
      socketId,
      brokerId: idBroker,
    };
    this.scheduleSocket.notification(data);
  }

  private parseDate(hours: string, minutes: string): string {
    const newDate = this.preSelectedDate
      .set({
        hours: Number(hours),
        minutes: Number(minutes),
        seconds: 0,
      })
      .local()
      .format();
    return newDate;
  }

  public validateForm(): void {
    this.isOpenAnswer = false;
    const openAnswerItem = this.questionI.answers.find((x) => x.isOpen);
    if (openAnswerItem) {
      this.isOpenAnswer = openAnswerItem.text === this.answer;
    }
    this.service.saveDataInProgress(
      {
        question: this.questionI.question,
        answer: this.isOpenAnswer ? this.openAnswer : this.answer,
      },
      TypeDataInProgress.preferences
    );
    if (
      !this.answer ||
      !this.scheduleSelected ||
      (this.isOpenAnswer && !this.openAnswer)
    ) {
      this.canUserContinue.next(false);
      return;
    }
    this.canUserContinue.next(true);
  }

  public focusOpen(): void {
    this.answer = this.questionI.answers.find((x) => x.isOpen).text;
    this.validateForm();
  }

  private getQuestion(): void {
    this.service.getQuestion().subscribe((response) => {
      this.questionI = response.response;
    });
  }
}
